import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from '../../../icons/icon-provider';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { DashIconComponent } from '../../../features/dashboards/shared/dash-icon.component';

/** Minimal shape required by the apps menu. The nav-bar's existing `appsList`
 *  already matches this (it also has an `icon` SVG string field which we ignore). */
export interface AppsMenuItem {
  id: string;
  label: string;
  route?: string;
  hidden?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-apps-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownComponent, NgIconComponent, DashIconComponent],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './apps-menu.component.html',
  styleUrls: ['./apps-menu.component.scss'],
})
export class AppsMenuComponent {
  @Input() apps: AppsMenuItem[] = [];
  @Input() title = 'Apps & Tools';
  @Input() manageLabel = 'Manage Apps →';
  @Input() activeAppId?: string;

  @Output() toggled  = new EventEmitter<boolean>();
  @Output() navigate = new EventEmitter<AppsMenuItem>();
  @Output() manage   = new EventEmitter<void>();

  @ViewChild('dd') dd!: DropdownComponent;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  query = '';

  /** Mapping from the nav-bar's existing app ids to Tabler icon names. */
  readonly iconMap: Record<string, string> = {
    'community':        'tablerBuildingCommunity',
    'admin-center':     'tablerSettings',
    'contract-center':  'tablerBriefcase',
    'billing-center':   'tablerCreditCard',
    'job-center':       'tablerBriefcase',
    'talent-center':    'tablerUsers',
    'applicants':       'tablerUserCheck',
    'business':         'tablerBuildingSkyscraper',
    'business-network': 'tablerNetwork',
    'workspaces':       'tablerSitemap',
    'member-network':   'tablerUsersGroup',
    'my-network':       'tablerUsers',
    'my-connections':   'tablerUsersGroup',
  };

  constructor(private cdr: ChangeDetectorRef) {}

  get visibleApps(): AppsMenuItem[] {
    return (this.apps || []).filter(a => !a.hidden);
  }

  /** Number of grid columns. A row holds at most 7, and the layout never
   *  exceeds 2 rows. So up to 7 apps sit in a single row (cols = count); beyond
   *  that they wrap into 2 rows (cols = ceil(count / 2), clamped to 7). Based on
   *  the full visible set — not the filtered set — so the panel doesn't reflow
   *  while the user types in the search box. */
  get gridCols(): number {
    const n = this.visibleApps.length;
    if (n <= 1) return 1;
    if (n <= 7) return n;
    return Math.min(7, Math.ceil(n / 2));
  }

  /** Panel width tracks the column count (~100px per column + grid padding),
   *  with a floor so the header (title + search pill) never looks cramped. */
  get panelWidth(): string {
    return `${Math.max(380, this.gridCols * 100 + 24)}px`;
  }

  get filteredApps(): AppsMenuItem[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.visibleApps;
    return this.visibleApps.filter(a => (a.label || '').toLowerCase().includes(q));
  }

  iconFor(app: AppsMenuItem): string {
    return this.iconMap[app.id] || 'tablerLayoutGrid';
  }

  onItemClick(app: AppsMenuItem) {
    this.navigate.emit(app);
    this.dd?.close();
  }

  onManageClick() {
    this.manage.emit();
    this.dd?.close();
  }

  onToggleChange(open: boolean) {
    this.toggled.emit(open);
    if (open) {
      // reset filter state each time the panel opens
      this.query = '';
      // focus the search input once the panel is actually visible
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }
  }
}
